import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  MapPin,
  CaretDown,
  CaretUp,
  Microphone,
  Camera,
  Receipt,
  Info,
  ChatCircleDots,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../../lib/api";
import { usePaywall } from "../../lib/paywall";
import { useAuth } from "../../lib/auth";
import { getUserId } from "../../lib/user";
import {
  getCountry,
  getDefaultCountryCode,
  rememberCountryCode,
  COUNTRIES,
} from "../../lib/tipping";
import { formatMoney, useExchangeRate, getHomeCurrency } from "../../lib/currency";
import { fitClass as fitClassImport } from "../../lib/fitClass";
import { LandmarkIllustration } from "./illustrations";

/**
 * Tip panel — matches the "START HERE" mockup pixel-close.
 * Three visual blocks:
 *   1) "Where are you?" card with the country picker pill + tipping
 *      guide on the left, and a colorful NYC illustration on the right.
 *   2) 4-up shortcut grid (Speak / Scan menu / Convert cash / Ask phrase).
 *   3) Dark navy BILL AMOUNT card with $0.00 + USD dropdown underline,
 *      tip slider, GOOD label, and a single dark-green Tip Amount + Total
 *      tile with a vertical divider.
 */
export default function TipPanel({ onNavigate, onSaved, autoScanReceipt = false, onActionConsumed }) {
  const [bill, setBill] = useState("");
  const [collapsed, setCollapsed] = useState(true); // start collapsed; show only Bill Amount + Scan
  const [countryCode, setCountryCode] = useState(() => getDefaultCountryCode());
  const country = useMemo(() => getCountry(countryCode), [countryCode]);
  const [showGuide, setShowGuide] = useState(false);

  // Initial tip percentage = the LOW end of the country's recommended
  // range so a "minimum tipper" never has to touch the slider in any
  // country. Clamped to the 0-50% slider domain.
  const [tipPct, setTipPct] = useState(() =>
    Math.max(0, Math.min(50, country?.range?.[0] ?? country?.defaultPct ?? 18))
  );
  const [people, setPeople] = useState(1);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);

  const fileRef = useRef(null);
  const userId = useMemo(() => getUserId(), []);

  // Auto-trigger receipt scan when arriving from the Saved tab's
  // "Scan receipt" promo CTA (`navigate("home:scan-receipt")`).
  const autoScanTriggeredRef = useRef(false);
  useEffect(() => {
    if (autoScanReceipt && !autoScanTriggeredRef.current) {
      autoScanTriggeredRef.current = true;
      setTimeout(() => {
        fileRef.current?.click();
        onActionConsumed?.();
      }, 200);
    }
  }, [autoScanReceipt, onActionConsumed]);
  const { handle402 } = usePaywall();
  const { refresh: refreshAuth } = useAuth();

  const localCurrency = country?.currency || "USD";
  const symbol = country?.currencySymbol || "$";

  // Home-currency conversion so the user can see what they're really paying
  // in the currency they think in (e.g. "≈ $2.40 USD" when bill is ¥355).
  const homeCurrency = useMemo(() => getHomeCurrency(), []);
  const fx = useExchangeRate(localCurrency, homeCurrency);
  const showHome = homeCurrency !== localCurrency && fx.rate;

  const billNum = parseFloat(bill) || 0;
  const tipAmt = useMemo(() => (billNum * tipPct) / 100, [billNum, tipPct]);
  const total = billNum + tipAmt;
  const perPerson = people > 0 ? total / people : total;

  const tipAmtHome = showHome ? tipAmt * fx.rate : null;
  const totalHome = showHome ? total * fx.rate : null;

  // Shared with TipPanel — see /app/frontend/src/lib/fitClass.js
  const fitClass = fitClassImport;
  const tipText = formatMoney(tipAmt, localCurrency);
  const totalText = formatMoney(total, localCurrency);

  // Bill amount also auto-shrinks so big-number currencies (¥12500, ₹35000)
  // OR long currency symbols (TT$, NT$, HK$, kr) don't bunch / clip.
  // We factor BOTH the numeric value length AND the symbol length so the
  // combined "TT$36.08" string never overflows the dark card horizontally.
  const billDisplay = bill || "0.00";
  const combinedLen = billDisplay.length + (symbol?.length || 0);
  const billFitClass = (() => {
    if (combinedLen >= 12) return "text-xl";
    if (combinedLen >= 10) return "text-2xl";
    if (combinedLen >= 9) return "text-3xl";
    if (combinedLen >= 7) return "text-4xl";
    return "text-5xl";
  })();
  const billSymbolClass = (() => {
    if (combinedLen >= 12) return "text-xl pb-0.5";
    if (combinedLen >= 10) return "text-2xl pb-0.5";
    if (combinedLen >= 9) return "text-3xl pb-1";
    if (combinedLen >= 7) return "text-4xl pb-1";
    return "text-5xl pb-1.5";
  })();

  // Tip notes (returned in the previous build).
  const [note, setNote] = useState("");

  function changeCountry(code) {
    setCountryCode(code);
    rememberCountryCode(code);
    const c = getCountry(code);
    if (c) setTipPct(Math.max(0, Math.min(50, c.range?.[0] ?? c.defaultPct ?? 18)));
  }

  async function onPickReceipt(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/^image\//.test(file.type)) {
      toast.error("Please upload an image");
      return;
    }
    setScanning(true);
    try {
      const dataUrl = await readAsDataUrl(file);
      const b64 = dataUrl.split(",", 2)[1];
      const r = await api.post(`/receipt/scan`, {
        image_base64: b64,
        mime: file.type || "image/jpeg",
      });
      if (r.data.bill_amount != null) {
        setBill(String(r.data.bill_amount));
        toast.success(`Detected ${formatMoney(r.data.bill_amount, localCurrency)}`);
      } else {
        toast.message("Couldn't read total — enter manually");
      }
    } catch (err) {
      if (!handle402(err)) {
        toast.error(err?.response?.data?.detail || "Scan failed");
      }
    } finally {
      setScanning(false);
      refreshAuth();
    }
  }

  async function saveTip() {
    if (billNum <= 0) {
      toast.error("Enter a bill amount first");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/tips`, {
        user_id: userId,
        bill: billNum,
        tip_pct: tipPct,
        tip_amount: Number(tipAmt.toFixed(2)),
        people,
        total: Number(total.toFixed(2)),
        per_person: Number(perPerson.toFixed(2)),
        country_code: countryCode,
        currency: localCurrency,
        home_currency: homeCurrency,
        home_rate: fx.rate || 1,
        receipt_base64: null,
        note: note.trim() || null,
      });
      toast.success("Saved to tip log");
      setBill("");
      setNote("");
      setTipPct(Math.max(0, Math.min(50, country?.range?.[0] ?? country?.defaultPct ?? 18)));
      setPeople(1);
      onSaved?.();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  }

  // Tip slider — 0% → 50% domain. The country's recommended range is
  // surfaced visually as a translucent ORANGE BAND drawn directly on the
  // slider track. The thumb fill stays white so the user always sees
  // exactly where they are vs the recommended band.
  const SLIDER_MIN = 0;
  const SLIDER_MAX = 50;
  const SLIDER_RANGE = SLIDER_MAX - SLIDER_MIN;
  const recMin = country?.range?.[0] ?? 15;
  const recMax = country?.range?.[1] ?? 25;
  const recStartStop = ((recMin - SLIDER_MIN) / SLIDER_RANGE) * 100;
  const recEndStop = ((recMax - SLIDER_MIN) / SLIDER_RANGE) * 100;
  const inRange = tipPct >= recMin && tipPct <= recMax;
  // Slider track gradient: faint white base → solid orange band over the
  // recommended range → faint white tail. The thumb's CSS handles its own
  // fill (white) via the `tip-slider-dark` class.
  const trackStyle = {
    background: `linear-gradient(to right,
      rgba(255,255,255,0.18) 0%,
      rgba(255,255,255,0.18) ${recStartStop}%,
      rgba(229,91,44,0.85) ${recStartStop}%,
      rgba(229,91,44,0.85) ${recEndStop}%,
      rgba(255,255,255,0.18) ${recEndStop}%,
      rgba(255,255,255,0.18) 100%)`,
  };

  return (
    <div className="space-y-4" data-testid="tip-panel">
      {/* "Where are you?" card with NYC illustration */}
      <div className="bg-white rounded-2xl shadow-soft border border-navy/5 p-4 overflow-hidden">
        <div className="flex items-stretch gap-3">
          {/* Left column: title + country picker + tipping guide + note */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <MapPin size={18} weight="fill" color="#0E2C54" />
              <span className="font-jakarta font-bold text-navy text-base">
                Where are you?
              </span>
            </div>

            <div className="relative mt-2 inline-block w-full align-top">
              <div className="bg-white border border-navy/15 rounded-full pl-3 pr-3 py-2 flex items-center gap-2 shadow-soft w-full">
                <span className="text-base leading-none">{country?.flag}</span>
                <span className="flex-1 min-w-0 font-jakarta font-semibold text-navy text-sm truncate">
                  {country?.name}
                </span>
                <CaretDown size={14} weight="bold" color="#0E2C54" />
              </div>
              <select
                data-testid="country-select"
                value={countryCode}
                onChange={(e) => changeCountry(e.target.value)}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                aria-label="Pick country"
              >
                {COUNTRIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {`${c.flag} ${c.name} — ${c.defaultPct}%`}
                  </option>
                ))}
              </select>
            </div>

            <div className="mt-3 text-sm font-jakarta font-bold text-navy">
              Tipping guide:{" "}
              <span className="text-orange">
                {country?.range?.[0]}–{country?.range?.[1]}%
              </span>
            </div>
            <div className="text-navy-soft text-[11px] mt-0.5 leading-snug font-jakarta">
              {country?.note}
            </div>
          </div>

          {/* Right column: illustration, larger so it reaches close to the picker pill */}
          <div className="shrink-0 w-[52%] self-stretch flex items-center justify-end pointer-events-none">
            <LandmarkIllustration
              code={countryCode}
              flag={country?.flag || "🌍"}
              className="w-full h-full object-contain"
            />
          </div>
        </div>
      </div>

      {/* 4 shortcut cards */}
      <div className="grid grid-cols-4 gap-2.5">
        <Shortcut
          iconSrc="/assets/quick-phrases-icon.jpg"
          label="Quick Phrases"
          sub="Travel phrase book"
          onClick={() => onNavigate?.("phrases")}
          testId="shortcut-quick-phrases"
        />
        <Shortcut
          iconSrc="/assets/scan-menu-icon.jpg"
          label="Scan menu"
          sub="Translate food instantly"
          onClick={() => onNavigate?.("translate:menu")}
          testId="shortcut-scan-menu"
        />
        <Shortcut
          iconSrc="/assets/travel-vault-icon.jpg"
          label="Travel Vault"
          sub="IDs, cards & trips"
          onClick={() => onNavigate?.("vault")}
          testId="shortcut-travel-vault"
        />
        <Shortcut
          iconSrc="/assets/tsa-rules-icon.jpg"
          label="TSA Rules"
          sub="Safety & security"
          onClick={() => onNavigate?.("tsa")}
          testId="shortcut-tsa-rules"
        />
      </div>

      {/* DARK NAVY BILL CARD */}
      <div
        data-testid="bill-card"
        className="bg-navy text-white rounded-3xl p-4 shadow-float"
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-white/70 text-[11px] uppercase tracking-[0.18em] font-jakarta font-bold">
              Bill Amount
            </div>
            <div className="flex items-baseline gap-1 mt-1 min-w-0 py-1">
              <span className={`font-jakarta font-extrabold text-white shrink-0 ${billSymbolClass}`}>
                {symbol}
              </span>
              <input
                data-testid="bill-input"
                inputMode="decimal"
                type="text"
                value={bill}
                onChange={(e) => {
                  const v = e.target.value.replace(/[^0-9.]/g, "");
                  setBill(v);
                  // Auto-expand the calculator the moment a bill is entered.
                  if (v && parseFloat(v) > 0 && collapsed) setCollapsed(false);
                }}
                placeholder="0.00"
                className={`flex-1 min-w-0 bg-transparent font-extrabold text-white placeholder:text-white/85 outline-none font-jakarta leading-tight ${billFitClass}`}
              />
            </div>
          </div>
          <div className="shrink-0 flex flex-col items-end gap-2">
            <button
              type="button"
              data-testid="scan-receipt-btn"
              onClick={() => fileRef.current?.click()}
              disabled={scanning}
              className="inline-flex items-center gap-1.5 bg-transparent border border-white/30 rounded-full px-3 py-1.5 text-xs font-bold"
            >
              <Camera size={14} weight="duotone" />
              {scanning ? "Scanning…" : "Scan receipt"}
            </button>
          </div>
          <input
            type="file"
            ref={fileRef}
            accept="image/*"
            capture="environment"
            onChange={onPickReceipt}
            className="hidden"
            data-testid="receipt-file-input"
          />
        </div>

        {!collapsed && (
        <>
        {/* Underline + currency selector */}
        <div className="mt-2 pt-2 border-t border-white/15 flex items-center justify-between">
          {showHome && billNum > 0 ? (
            <div className="text-white/60 text-sm font-jakarta font-semibold">
              ≈ {formatMoney(billNum * fx.rate, homeCurrency)}{" "}
              <span className="text-white/40 text-xs">{homeCurrency}</span>
              {fx.stale && <span className="text-orange/70 ml-1 text-xs">· cached</span>}
            </div>
          ) : (
            <div />
          )}
          <div className="relative">
            <div className="flex items-center gap-1 text-white text-sm font-jakarta font-bold">
              {localCurrency}
              <CaretDown size={14} weight="bold" />
            </div>
            <select
              data-testid="currency-select"
              value={countryCode}
              onChange={(e) => changeCountry(e.target.value)}
              className="absolute inset-0 opacity-0 cursor-pointer"
              aria-label="Pick currency"
            >
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {`${c.flag} ${c.currency} — ${c.name}`}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Slider header */}
        <div className="flex items-center justify-between mt-4">
          <button
            type="button"
            onClick={() => setShowGuide(true)}
            data-testid="recommended-tip-info-btn"
            aria-label="Tipping guide"
            className="flex items-center gap-1.5 text-white/70 hover:text-white text-[11px] uppercase tracking-[0.18em] font-jakarta font-bold transition-colors"
          >
            Recommended Tip
            <Info size={14} weight="duotone" />
          </button>
          <div className="font-jakarta font-extrabold text-orange text-4xl leading-none">
            {tipPct}%
          </div>
        </div>

        <input
          data-testid="tip-slider"
          type="range"
          min={SLIDER_MIN}
          max={SLIDER_MAX}
          step="1"
          value={Math.max(SLIDER_MIN, Math.min(SLIDER_MAX, tipPct))}
          onChange={(e) => setTipPct(parseInt(e.target.value, 10))}
          className="tip-slider-dark w-full mt-3"
          style={trackStyle}
        />
        <div className="flex justify-between mt-2 text-white/60 text-xs font-jakarta font-semibold">
          <span>0%</span>
          <span
            className="text-orange font-extrabold tracking-[0.18em]"
            data-testid="tip-service-label"
          >
            {serviceLabel(tipPct, recMin, recMax)}
          </span>
          <span>50%</span>
        </div>

        {/* Combined forest-green Tip + Total tile — clean solid color
            matching the BrandMark currency dot, no muddy gradient. */}
        <div className="mt-5 bg-forest rounded-2xl grid grid-cols-2 overflow-hidden">
          <div className="p-4 min-w-0">
            <div className="text-white/85 text-[10px] uppercase tracking-[0.2em] font-jakarta font-bold">
              Tip Amount
            </div>
            <div
              className={`font-jakarta font-extrabold leading-tight mt-1 truncate ${fitClass(tipText)}`}
              style={{ color: "#5DD16F" }}
              title={tipText}
            >
              {tipText}
            </div>
            {showHome && (
              <div className="text-white/80 text-[11px] font-jakarta font-semibold mt-0.5 truncate">
                ≈ {formatMoney(tipAmtHome, homeCurrency)}{" "}
                {fx.stale && <span className="text-white/50">· cached</span>}
              </div>
            )}
          </div>
          <div className="p-4 border-l border-white/15 min-w-0">
            <div className="text-white/85 text-[10px] uppercase tracking-[0.2em] font-jakarta font-bold">
              Total
            </div>
            <div
              className={`font-jakarta font-extrabold text-white leading-tight mt-1 truncate ${fitClass(totalText)}`}
              title={totalText}
            >
              {totalText}
            </div>
            {showHome && (
              <div className="text-white/80 text-[11px] font-jakarta font-semibold mt-0.5 truncate">
                ≈ {formatMoney(totalHome, homeCurrency)}
              </div>
            )}
          </div>
        </div>

        {/* Split (Save button moved OUT of dark card so the note can sit between them) */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-white/10">
          <div>
            <div className="text-white/60 text-[10px] uppercase tracking-wider font-jakarta font-bold">
              Split between
            </div>
            <div className="text-white text-sm mt-0.5 font-jakarta font-semibold">
              {formatMoney(perPerson, localCurrency)} each
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="people-decrement"
              onClick={() => setPeople((p) => Math.max(1, p - 1))}
              className="w-8 h-8 rounded-full border-2 border-white/30 text-white text-xl font-bold flex items-center justify-center hover:bg-white/10"
            >
              −
            </button>
            <span
              data-testid="people-count"
              className="text-white font-jakarta font-extrabold text-xl w-6 text-center"
            >
              {people}
            </span>
            <button
              type="button"
              data-testid="people-increment"
              onClick={() => setPeople((p) => Math.min(20, p + 1))}
              className="w-8 h-8 rounded-full border-2 border-white/30 text-white text-xl font-bold flex items-center justify-center hover:bg-white/10"
            >
              +
            </button>
          </div>
        </div>
        </>
        )}

        {/* Bottom-right collapse / expand toggle — kept far from Scan Receipt */}
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            data-testid="tip-card-toggle"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? "Expand calculator" : "Collapse calculator"}
            className="inline-flex items-center justify-center gap-1.5 text-white/70 hover:text-white text-[11px] font-jakarta font-bold uppercase tracking-wider bg-white/5 hover:bg-white/10 border border-white/15 rounded-full px-4 py-1.5 transition-colors"
          >
            {collapsed ? (
              <>
                Tap to expand
                <CaretDown size={12} weight="bold" />
              </>
            ) : (
              <>
                Collapse
                <CaretUp size={12} weight="bold" />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Notes input — sits below the navy card. */}
      {!collapsed && (
      <div className="bg-white rounded-2xl shadow-soft border border-navy/5 p-4">
        <label
          htmlFor="tip-note"
          className="flex items-center gap-2 text-navy font-jakarta font-semibold text-sm"
        >
          <NoteIcon />
          Add a note (optional)
        </label>
        <textarea
          id="tip-note"
          data-testid="tip-note-input"
          value={note}
          onChange={(e) =>
            setNote(e.target.value.slice(0, 280))
          }
          rows={2}
          placeholder="Great service, anniversary dinner, server's name…"
          className="w-full mt-2 bg-bone/70 border border-navy/10 rounded-xl px-3 py-2 text-base text-navy outline-none resize-none placeholder:text-navy-soft font-jakarta font-medium focus:border-orange/40"
        />
        <div className="text-right text-navy-soft text-xs font-jakarta font-semibold mt-1">
          {note.length} / 280
        </div>
      </div>
      )}

      {/* Save tip CTA */}
      {!collapsed && (
      <button
        type="button"
        data-testid="save-tip-btn"
        onClick={saveTip}
        disabled={saving || billNum <= 0}
        className="w-full bg-orange hover:bg-orange-hover disabled:opacity-50 text-white font-jakarta font-extrabold text-base rounded-full py-3.5 transition-colors shadow-float"
      >
        {saving ? "Saving…" : "Save Tip"}
      </button>
      )}

      {/* Tipping-guide modal — explains the country's recommended range
          + cultural notes when the user taps the "i" next to the
          "Recommended Tip" header. */}
      {showGuide && (
        <div
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowGuide(false);
          }}
          data-testid="tipping-guide-modal"
          className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-start justify-center p-3 pt-6"
        >
          <div className="w-full max-w-md bg-white rounded-3xl shadow-deep p-5 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-[10px] uppercase tracking-[0.25em] font-jakarta font-bold text-orange">
                  Tipping guide
                </div>
                <h3 className="font-jakarta font-extrabold text-navy text-2xl mt-1 leading-tight">
                  {country?.flag} {country?.name || "This country"}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowGuide(false)}
                aria-label="Close"
                data-testid="tipping-guide-close-btn"
                className="w-9 h-9 rounded-full bg-bone/70 flex items-center justify-center shrink-0 font-jakarta font-extrabold text-navy"
              >
                ×
              </button>
            </div>

            <div className="bg-bone/50 rounded-2xl p-4">
              <div className="text-navy-soft text-[11px] uppercase tracking-widest font-jakarta font-bold">
                Recommended range
              </div>
              <div className="font-jakarta font-extrabold text-navy text-3xl mt-1">
                {recMin}% – {recMax}%
              </div>
              <div className="text-navy-soft text-xs font-jakarta mt-1">
                Locals typically tip in this range. The orange band on the slider
                marks the same window.
              </div>
            </div>

            {country?.note && (
              <div className="text-navy text-sm font-jakarta leading-relaxed">
                {country.note}
              </div>
            )}

            <button
              type="button"
              onClick={() => setShowGuide(false)}
              data-testid="tipping-guide-got-it-btn"
              className="w-full bg-navy text-white rounded-full py-3 font-jakarta font-extrabold text-sm active:scale-[0.98] transition-transform mt-1"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------- helpers ---------- */

function Shortcut({ icon, iconSrc, label, sub, onClick, testId }) {
  // Image-based shortcuts (Quick Phrases / Travel Vault / TSA Rules) ship
  // pre-rendered tile graphics with their own title + subtitle baked in —
  // render edge-to-edge with no extra text so we don't duplicate the label.
  if (iconSrc) {
    return (
      <button
        type="button"
        data-testid={testId}
        onClick={onClick}
        className="bg-white rounded-2xl shadow-soft border border-navy/5 p-1 flex items-center justify-center hover:shadow-float active:scale-95 transition-all h-[110px] overflow-hidden"
      >
        <img
          src={iconSrc}
          alt={label}
          loading="lazy"
          className="w-full h-full object-contain"
        />
      </button>
    );
  }
  // Inline-SVG shortcuts (Scan menu) render an icon + label + sub stack
  // styled to read like the baked-in tile graphics for visual uniformity.
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className="bg-white rounded-2xl shadow-soft border border-navy/5 px-1 py-2 flex flex-col items-center justify-center gap-1 hover:shadow-float active:scale-95 transition-all h-[110px]"
    >
      <div className="w-11 h-11 flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div className="text-navy font-jakarta font-extrabold text-[10.5px] leading-tight text-center">
        {label}
      </div>
      <div className="text-navy-soft text-[9px] leading-[1.15] text-center font-jakarta font-medium px-0.5">
        {sub}
      </div>
    </button>
  );
}

function SpeakShortcutIcon() {
  return (
    <div className="w-9 h-9 rounded-full bg-sky text-white flex items-center justify-center relative">
      <Microphone size={18} weight="fill" color="#fff" />
      <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-sky border-2 border-white" />
    </div>
  );
}

function QuickPhrasesShortcutIcon() {
  return (
    <div className="w-10 h-10 flex items-center justify-center">
      <svg width="38" height="38" viewBox="0 0 38 38" aria-hidden>
        {/* Speech-bubble book */}
        <path
          d="M7 9 Q7 6 10 6 H28 Q31 6 31 9 V24 Q31 27 28 27 H17 L12 32 V27 H10 Q7 27 7 24 Z"
          fill="#0E2C54"
        />
        {/* Open book pages */}
        <rect x="12" y="11" width="14" height="2" rx="1" fill="#fff" />
        <rect x="12" y="15" width="11" height="2" rx="1" fill="#fff" />
        <rect x="12" y="19" width="8" height="2" rx="1" fill="#fff" />
        {/* Accent dot */}
        <circle cx="29" cy="9" r="3" fill="#E55B2C" />
      </svg>
    </div>
  );
}

function TravelVaultShortcutIcon() {
  return (
    <div className="w-10 h-10 flex items-center justify-center">
      <svg width="38" height="38" viewBox="0 0 38 38" aria-hidden>
        {/* Vault body */}
        <rect x="8" y="9" width="22" height="24" rx="3" fill="#0E2C54" />
        <rect x="8" y="9" width="22" height="4" fill="#1B4279" />
        {/* Inner panel */}
        <rect x="11" y="15" width="16" height="14" rx="2" fill="#FFFFFF" opacity="0.95" />
        {/* Passport book */}
        <rect x="13" y="17" width="9" height="10" rx="1" fill="#0E2C54" />
        <circle cx="17.5" cy="22" r="2" fill="#FFD980" />
        {/* Lock dial */}
        <circle cx="25" cy="22" r="2.6" fill="#E55B2C" />
        <circle cx="25" cy="22" r="1" fill="#fff" />
      </svg>
    </div>
  );
}

function TSARulesShortcutIcon() {
  return (
    <div className="w-10 h-10 flex items-center justify-center">
      <svg width="38" height="38" viewBox="0 0 38 38" aria-hidden>
        {/* Shield */}
        <path
          d="M19 5 L31 9 V19 Q31 27 19 33 Q7 27 7 19 V9 Z"
          fill="#0E2C54"
        />
        <path
          d="M19 7.5 L29 10.5 V19 Q29 25.5 19 30.5 Q9 25.5 9 19 V10.5 Z"
          fill="#1B4279"
          opacity="0.4"
        />
        {/* Airplane */}
        <path
          d="M19 15 L25 19 L21 20 L19 25 L18 21 L13 19 Z"
          fill="#FFFFFF"
        />
        {/* Check mark */}
        <circle cx="27" cy="27" r="5" fill="#2E7D5A" />
        <path d="M24.5 27 L26.5 29 L29.5 25.5" stroke="#fff" strokeWidth="1.6" fill="none" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

function ScanMenuShortcutIcon() {
  // Navy flat menu-with-scan-corners icon to match the v1.3 shortcut row style.
  return (
    <div className="w-14 h-14 flex items-center justify-center">
      <svg width="52" height="52" viewBox="0 0 52 52" aria-hidden>
        {/* Menu card */}
        <rect x="11" y="9" width="22" height="30" rx="2.5" fill="#0E2C54" />
        {/* Menu page */}
        <rect x="14" y="13" width="16" height="22" rx="1.5" fill="#FFFFFF" />
        {/* Lines */}
        <rect x="16" y="16" width="10" height="2" rx="1" fill="#0E2C54" />
        <rect x="16" y="20" width="12" height="1.4" rx="0.7" fill="#0E2C54" opacity="0.45" />
        <rect x="16" y="23" width="9" height="1.4" rx="0.7" fill="#0E2C54" opacity="0.45" />
        <rect x="16" y="27" width="11" height="2" rx="1" fill="#E55B2C" />
        <rect x="16" y="31" width="8" height="1.4" rx="0.7" fill="#0E2C54" opacity="0.45" />
        {/* Scan-frame corner brackets (navy stroke) */}
        <g
          fill="none"
          stroke="#0E2C54"
          strokeWidth="2.4"
          strokeLinecap="round"
        >
          <path d="M6 14 V8.5 Q6 6 8.5 6 H14" />
          <path d="M38 6 H43.5 Q46 6 46 8.5 V14" />
          <path d="M46 34 V39.5 Q46 42 43.5 42 H38" />
          <path d="M14 42 H8.5 Q6 42 6 39.5 V34" />
        </g>
      </svg>
    </div>
  );
}

function ConvertShortcutIcon() {
  return (
    <div className="w-9 h-9 rounded-md bg-forest text-white flex items-center justify-center font-jakarta font-extrabold text-sm shadow-sm">
      $
    </div>
  );
}

function AskPhraseShortcutIcon() {
  return (
    <div className="w-9 h-9 rounded-full bg-navy text-white flex items-center justify-center relative">
      <ChatCircleDots size={20} weight="fill" color="#fff" />
    </div>
  );
}

function NoteIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden>
      <path
        d="M3 2.5C3 2.22386 3.22386 2 3.5 2H9.79289C9.92539 2 10.0526 2.05268 10.1464 2.14645L12.8536 4.85355C12.9473 4.94732 13 5.07452 13 5.20711V13.5C13 13.7761 12.7761 14 12.5 14H3.5C3.22386 14 3 13.7761 3 13.5V2.5Z"
        stroke="#0E2C54"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M9 2.5V5H12.5" stroke="#0E2C54" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function serviceLabel(pct, recMin = 15, recMax = 22) {
  // Encouragement scales relative to the *country's* recommended range,
  // so a 10% tip in France (good) reads differently than a 10% tip in
  // the USA (light). Uses 5 evenly spaced buckets across 0%–50%.
  if (pct <= 0) return "NO TIP";
  if (pct < recMin) {
    // Below recommended — gentle, never shaming
    if (pct < recMin / 2) return "LIGHT";
    return "FAIR";
  }
  // In or above recommended range
  if (pct <= recMin + (recMax - recMin) * 0.4) return "GOOD";
  if (pct <= recMax) return "GREAT";
  if (pct <= recMax + 5) return "EXCELLENT";
  if (pct <= recMax + 12) return "SUPERSTAR";
  return "LEGEND";
}

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}
